// The dispatch stage of an invoice, the same on every screen (tracker #94):
// Pending hand-off → Pending dispatch → Dispatched → Delivered, or Cancelled.
// Server: src/shared/dispatch/dispatch-stage.js.
import { Check, Clock3, PackageCheck, Truck, XCircle, Hand } from 'lucide-react';
import { cn } from '@/shared/utils/utils';

export type DispatchStage = 'not_handed_off' | 'awaiting_dispatch' | 'dispatched' | 'delivered' | 'cancelled';

export interface DispatchStatusInfo {
  stage: DispatchStage | string;
  label?: string;
  handedOffAt?: string | null;
  requiredDispatchDate?: string | null;
  overdueDays?: number;
  dispatchedAt?: string | null;
  deliveredAt?: string | null;
  challanNumber?: string;
  transporter?: string;
  courierName?: string;
  lrNumber?: string;
  vehicleNumber?: string;
}

const LOOK: Record<DispatchStage, { label: string; chip: string; dot: string; icon: typeof Truck }> = {
  not_handed_off: { label: 'Pending hand-off', chip: 'border-border bg-surface-container-low text-on-surface-variant', dot: 'bg-on-surface-subtle', icon: Hand },
  awaiting_dispatch: { label: 'Pending dispatch', chip: 'border-warning/30 bg-warning/10 text-warning-text', dot: 'bg-warning', icon: Clock3 },
  dispatched: { label: 'Dispatched', chip: 'border-info/30 bg-info/10 text-info-text', dot: 'bg-info', icon: Truck },
  delivered: { label: 'Delivered', chip: 'border-success/30 bg-success/10 text-success-text', dot: 'bg-success', icon: PackageCheck },
  cancelled: { label: 'Cancelled', chip: 'border-danger/25 bg-danger/10 text-danger-text', dot: 'bg-danger', icon: XCircle },
};
const OVERDUE = 'border-danger/30 bg-danger/10 text-danger-text';

const day = (v?: string | null) => {
  if (!v) return '';
  const d = new Date(v);
  return Number.isNaN(d.getTime()) ? '' : d.toLocaleDateString('en-IN', { day: 'numeric', month: 'short' });
};

function lookOf(ds?: DispatchStatusInfo | null) {
  const stage = (ds?.stage && ds.stage in LOOK ? ds.stage : 'not_handed_off') as DispatchStage;
  return { stage, ...LOOK[stage] };
}

/** One line of detail for the current stage. */
function detailOf(ds: DispatchStatusInfo, stage: DispatchStage) {
  if (stage === 'awaiting_dispatch') {
    if (ds.overdueDays && ds.overdueDays > 0) return `Overdue ${ds.overdueDays} day${ds.overdueDays === 1 ? '' : 's'} · due ${day(ds.requiredDispatchDate)}`;
    if (ds.requiredDispatchDate) return `Due ${day(ds.requiredDispatchDate)}`;
    return ds.handedOffAt ? `Handed off ${day(ds.handedOffAt)}` : '';
  }
  if (stage === 'dispatched') return [day(ds.dispatchedAt), ds.transporter || ds.courierName, ds.lrNumber ? `LR ${ds.lrNumber}` : ''].filter(Boolean).join(' · ');
  if (stage === 'delivered') return [ds.deliveredAt ? `on ${day(ds.deliveredAt)}` : '', ds.challanNumber ? `DC ${ds.challanNumber}` : ''].filter(Boolean).join(' · ');
  if (stage === 'not_handed_off') return 'Sales to hand over';
  return '';
}

/** Compact: a pill with the stage, and one line of what matters at that stage. */
export function DispatchStatusBadge({ status, className }: { status?: DispatchStatusInfo | null; className?: string }) {
  const { stage, label, chip, dot } = lookOf(status);
  const overdue = stage === 'awaiting_dispatch' && (status?.overdueDays || 0) > 0;
  const detail = status ? detailOf(status, stage) : '';
  return (
    <div className={cn('flex flex-col gap-0.5', className)}>
      <span className={cn('inline-flex w-fit items-center gap-1.5 whitespace-nowrap rounded-full border px-2.5 py-0.5 text-label-caps font-semibold', overdue ? OVERDUE : chip)}>
        <span className={cn('h-1.5 w-1.5 rounded-full', overdue ? 'bg-danger' : dot)} aria-hidden />
        {overdue ? 'Overdue' : label}
      </span>
      {detail && <span className={cn('whitespace-nowrap pl-1 text-[11px]', overdue ? 'font-medium text-danger-text' : 'text-on-surface-subtle')}>{detail}</span>}
    </div>
  );
}

const STEPS: { key: DispatchStage; label: string }[] = [
  { key: 'not_handed_off', label: 'Handed off' },
  { key: 'awaiting_dispatch', label: 'Pending dispatch' },
  { key: 'dispatched', label: 'Dispatched' },
  { key: 'delivered', label: 'Delivered' },
];
const ORDER: Record<string, number> = { not_handed_off: 0, awaiting_dispatch: 1, dispatched: 2, delivered: 3 };

/** Full: a four-step tracker with the date reached at each step. */
export function DispatchStatusTracker({ status }: { status?: DispatchStatusInfo | null }) {
  const { stage } = lookOf(status);
  if (stage === 'cancelled') return <DispatchStatusBadge status={status} />;
  const at = ORDER[stage] ?? 0;
  const dates = [status?.handedOffAt, status?.handedOffAt, status?.dispatchedAt, status?.deliveredAt];
  return (
    <div className="space-y-2">
      <ol className="flex items-start" aria-label={`Dispatch: ${LOOK[stage].label}`}>
        {STEPS.map((s, i) => {
          // Step 0 "Handed off" is done once the order reaches Pending dispatch.
          const done = i === 0 ? at >= 1 : i <= at;
          const current = i === 0 ? at === 0 : i === at;
          const Icon = done ? Check : (i === 0 ? Hand : i === 1 ? Clock3 : i === 2 ? Truck : PackageCheck);
          return (
            <li key={s.key} className="flex flex-1 flex-col items-center text-center">
              <div className="flex w-full items-center">
                <span className={cn('h-0.5 flex-1', i === 0 ? 'bg-transparent' : done ? 'bg-success' : 'bg-border')} />
                <span className={cn('flex h-7 w-7 shrink-0 items-center justify-center rounded-full border-2',
                  done ? 'border-success bg-success text-white' : current ? 'border-warning bg-warning/10 text-warning-text' : 'border-border bg-card text-on-surface-subtle')}>
                  <Icon className="h-3.5 w-3.5" aria-hidden />
                </span>
                <span className={cn('h-0.5 flex-1', i === STEPS.length - 1 ? 'bg-transparent' : i < at ? 'bg-success' : 'bg-border')} />
              </div>
              <span className={cn('mt-1 text-[11px] font-semibold', done || current ? 'text-on-surface' : 'text-on-surface-subtle')}>{s.label}</span>
              <span className="text-[10px] text-on-surface-subtle">{done && i !== 1 ? day(dates[i]) : ''}</span>
            </li>
          );
        })}
      </ol>
      {status && detailOf(status, stage) && <p className="text-center text-body-sm text-on-surface-variant">{detailOf(status, stage)}</p>}
    </div>
  );
}

export default DispatchStatusBadge;
